import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import EditProfileModal from './EditProfileModal';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  FileSpreadsheet,
  Banknote,
  Truck,
  Users,
  History,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Camera,
  Settings,
  Link as LinkIcon,
  Copy,
  ClipboardCheck,
  FileCheck,
  UserCheck,
  Activity,
  FileBarChart
} from 'lucide-react';

const Sidebar = ({ children }) => {
  const { user, logout, hasRole, hasAnyRole, updateUserAvatar } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const menuItems = [
    {
      title: 'Home',
      icon: LayoutDashboard,
      path: '/dashboard',
      roles: 'all'
    },
    {
      title: 'Quotes',
      icon: FileSpreadsheet,
      path: '/quotes',
      roles: 'all'
    },
    {
      title: 'Upload Quote',
      icon: FileSpreadsheet,
      path: '/quotes/upload',
      roles: ['Key Account Manager', 'Super Admin', 'Project Head']
    },
    {
      title: 'Fund Requests',
      icon: Banknote,
      path: '/fund-requests',
      roles: 'all'
    },
    {
      title: 'Material Requests',
      icon: Truck,
      path: '/material-requests',
      roles: 'all'
    },
    {
      title: 'Quote Workflow',
      icon: ClipboardCheck,
      path: '/quote-workflow',
      roles: ['Site Supervisor', 'Super Admin']
    },
    {
      title: 'Billing',
      icon: FileSpreadsheet,
      path: '/billing',
      roles: ['Billing Manager', 'Billing Executive', 'Account Manager', 'Delivery Manager', 'Project Head', 'Key Account Manager', 'Super Admin'],
      subItems: [
        {
          title: 'Joint Measurement Billing',
          path: '/billing',
          roles: ['Billing Manager', 'Billing Executive', 'Account Manager', 'Project Head', 'Key Account Manager', 'Super Admin']
        },
        {
          title: 'Billing of Material',
          path: '/billing/material',
          roles: ['Account Manager', 'Delivery Manager', 'Super Admin']
        }
      ]
    },
    {
      title: 'Jobsheet Review',
      icon: FileCheck,
      path: '/jobsheet-review',
      roles: ['Billing Manager', 'Super Admin']
    },
    {
      title: 'AFM Validation',
      icon: UserCheck,
      path: '/afm-validation',
      roles: ['Billing Manager', 'Project Head', 'Key Account Manager', 'Super Admin']
    },
    {
      title: 'Analytics',
      icon: LayoutDashboard,
      path: '/analytics',
      roles: ['Project Head', 'Key Account Manager', 'Super Admin']
    },
    {
      title: 'User Management',
      icon: Users,
      path: '/users',
      roles: ['Super Admin', 'Project Head', 'Key Account Manager']
    },
    {
      title: 'Audit Logs',
      icon: History,
      path: '/audit-logs',
      roles: ['Super Admin', 'Project Head', 'Key Account Manager']
    },
    {
      title: 'Status Updates',
      icon: Activity,
      path: '/status-updates',
      roles: 'all'
    },
    {
      title: 'Audit Reports',
      icon: FileBarChart,
      path: '/audit-reports',
      roles: ['Super Admin']
    }
  ];

  const isActive = (path) => location.pathname === path;
  const isSubItemActive = (subItems) => subItems?.some(sub => location.pathname === sub.path);

  // State for expanded submenus
  const [expandedMenus, setExpandedMenus] = useState(() => {
    // Initialize with menus that have active sub-items
    const expanded = {};
    menuItems.forEach(item => {
      if (item.subItems && isSubItemActive(item.subItems)) {
        expanded[item.title] = true;
      }
    });
    return expanded;
  });

  const toggleSubmenu = (title) => {
    setExpandedMenus(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const shouldShowMenuItem = (roles) => {
    if (roles === 'all') return true;
    return hasAnyRole(roles);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getProfileUrl = (name) => {
    const slug = name?.toLowerCase().replace(/\s+/g, '-') || 'user';
    return `radiantsco.com/demo/${slug}`;
  };

  const copyProfileUrl = () => {
    const url = `https://${getProfileUrl(user?.name)}`;
    navigator.clipboard.writeText(url);
    toast.success('Portal URL copied to clipboard!');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b bg-gradient-to-r from-[#1B273D] to-[#2d3e5f] flex-shrink-0">
        <h2 className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Radiants Civiletric
        </h2>
        <p className="text-xs text-gray-200 mt-1 font-medium">Let The Spark Begins</p>
      </div>

      {/* User Info */}
      <div className="p-4 border-b bg-gradient-to-br from-white/90 to-gray-50/80 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Clickable Avatar */}
          <div 
            className="relative group cursor-pointer"
            onClick={() => setEditProfileOpen(true)}
            data-testid="avatar-upload-trigger"
          >
            <Avatar className="h-12 w-12 ring-2 ring-[#DC143C] ring-offset-2 transition-all group-hover:ring-4">
              {user?.avatar_url ? (
                <AvatarImage 
                  src={`${BACKEND_URL}${user.avatar_url}`} 
                  alt={user?.name}
                  className="object-cover"
                />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-[#DC143C] to-[#b91130] text-white text-lg font-bold">
                {getInitials(user?.name || 'U')}
              </AvatarFallback>
            </Avatar>
            {/* Camera overlay on hover */}
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate text-gray-800">{user?.name}</p>
            <p className="text-xs text-gray-600 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {user?.roles?.slice(0, 2).map((role, index) => (
            <Badge key={index} className="text-[10px] px-2 py-1 bg-[#1B273D] text-white border-0 font-semibold">
              {role}
            </Badge>
          ))}
          {user?.roles?.length > 2 && (
            <Badge className="text-[10px] px-2 py-1 bg-gray-200 text-gray-700 border-0 font-semibold">
              +{user.roles.length - 2}
            </Badge>
          )}
        </div>

        {/* Edit Profile Button */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 text-xs text-gray-600 hover:text-[#DC143C] hover:bg-[#DC143C]/5"
          onClick={() => setEditProfileOpen(true)}
          data-testid="edit-profile-btn"
        >
          <Settings className="h-3 w-3 mr-2" />
          Edit Profile Picture
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 p-3 space-y-1 bg-gray-50 overflow-y-auto min-h-0">
        {menuItems.map((item) => {
          if (!shouldShowMenuItem(item.roles)) return null;

          // Check if item has subItems
          if (item.subItems && item.subItems.length > 0) {
            const visibleSubItems = item.subItems.filter(sub => shouldShowMenuItem(sub.roles));
            if (visibleSubItems.length === 0) return null;
            
            const isSubMenuActive = visibleSubItems.some(sub => isActive(sub.path));
            const isExpanded = expandedMenus[item.title] || isSubMenuActive;
            
            return (
              <div key={item.title}>
                <button
                  onClick={() => toggleSubmenu(item.title)}
                  className={
                    `w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                      isSubMenuActive
                        ? 'bg-gradient-to-r from-[#DC143C] to-[#b91130] text-white shadow-lg scale-[1.02]'
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`
                  }
                  data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`h-4 w-4 ${isSubMenuActive ? '' : 'group-hover:scale-110 transition-transform'}`} />
                  <span className="flex-1 text-left">{item.title}</span>
                  <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {visibleSubItems.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={
                          `flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                            isActive(subItem.path)
                              ? 'bg-[#DC143C]/10 text-[#DC143C] font-semibold'
                              : 'text-gray-600 hover:bg-white hover:text-gray-800'
                          }`
                        }
                        data-testid={`nav-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        <span>{subItem.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-[#DC143C] to-[#b91130] text-white shadow-lg scale-[1.02]'
                    : 'text-gray-700 hover:bg-white hover:shadow-md'
                }`
              }
              data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className={`h-4 w-4 ${isActive(item.path) ? '' : 'group-hover:scale-110 transition-transform'}`} />
              <span className="flex-1">{item.title}</span>
              {isActive(item.path) && <ChevronRight className="h-4 w-4" />}
            </Link>
          );
        })}
      </nav>

      {/* Logout - Always visible at bottom */}
      <div className="p-3 border-t bg-gray-50 flex-shrink-0">
        <Button
          variant="ghost"
          className="w-full justify-start hover:bg-red-50 hover:text-red-600 text-gray-700 font-semibold transition-colors py-2.5"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="mr-3 h-5 w-5" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col fixed left-0 top-0 z-40 h-screen w-64 border-r bg-background">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed left-0 top-0 z-50 h-screen w-64 border-r bg-background lg:hidden flex flex-col">
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Mobile Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Radiants Civiletric
          </h1>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8 lg:p-12 bg-gradient-to-br from-gray-50/80 via-white to-gray-100/80 min-h-screen">
          {children}
        </main>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={editProfileOpen}
        onOpenChange={setEditProfileOpen}
        user={user}
        onAvatarUpdate={updateUserAvatar}
      />
    </div>
  );
};

export default Sidebar;
